import React from 'react';
import { Button, TextInput, Textarea, Select, NumberInput, Paper, Stack, Text, TagsInput, Title } from '@mantine/core';

export const categoryOptions = [
  { value: '0', label: 'Unknown' },
  { value: '1', label: 'Movie' },
  { value: '2', label: 'Video Game' },
  { value: '3', label: 'Book' },
  { value: '4', label: 'YouTube Channel' },
];

export const subCategoryOptions = [
  { value: '', label: 'None' },
  { value: 'movie', label: 'Movie' },
  { value: 'series', label: 'Series' },
  { value: 'anime', label: 'Anime' },
  { value: 'cartoon', label: 'Cartoon' },
  { value: 'documentary', label: 'Documentary' },
];

const ArtForm = ({ form, setField, onSubmit, loading, error, success, submitLabel, requireWikiLink }) => {
  return (
    <form onSubmit={onSubmit}>
      <Stack gap="sm">
        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Title order={4}>Basic Info</Title>
            <TextInput label="Name *" required value={form.name} onChange={e => setField('name', e.target.value)} />
            <Textarea label="Description" autosize minRows={2} value={form.description} onChange={e => setField('description', e.target.value)} />
            <TextInput label="RU Name" value={form.ruName} onChange={e => setField('ruName', e.target.value)} />
            <Textarea label="RU Description" autosize minRows={2} value={form.ruDescription} onChange={e => setField('ruDescription', e.target.value)} />
            <Select label="Category" data={categoryOptions} value={form.category} onChange={v => setField('category', v)} />
            <Select label="Sub Category" data={subCategoryOptions} value={form.subCategory} onChange={v => setField('subCategory', v || '')} clearable />
            <NumberInput label="Year" value={form.year} onChange={v => setField('year', v)} />
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Title order={4}>Links</Title>
            <TextInput label={requireWikiLink ? "Wiki Article Link *" : "Wiki Article Link"} required={!!requireWikiLink} value={form.wikiArticleLink} onChange={e => setField('wikiArticleLink', e.target.value)} />
            <TextInput label="Wiki Article ID" value={form.wikiArticleID} onChange={e => setField('wikiArticleID', e.target.value)} />
            <TextInput label="RU Wiki Article Link" value={form.ruWikiArticleLink} onChange={e => setField('ruWikiArticleLink', e.target.value)} />
            <TextInput label="Thumbnail URL" value={form.thumbnailUrl} onChange={e => setField('thumbnailUrl', e.target.value)} />
            <TagsInput label="Other Platform Links" value={form.otherPlatformsLinks} onChange={v => setField('otherPlatformsLinks', v)} placeholder="Enter URL and press Enter" />
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Title order={4}>Details</Title>
            <TextInput label="Author / Developer" value={form.author} onChange={e => setField('author', e.target.value)} />
            <TextInput label="Publisher" value={form.publisher} onChange={e => setField('publisher', e.target.value)} />
            <TextInput label="Country" value={form.country} onChange={e => setField('country', e.target.value)} />
            <NumberInput label="Duration (minutes)" value={form.duration} onChange={v => setField('duration', v)} />
            <TagsInput label="Genres" value={form.genres} onChange={v => setField('genres', v)} placeholder="Enter genre and press Enter" />
            <TagsInput label="Actors" value={form.actors} onChange={v => setField('actors', v)} placeholder="Enter actor and press Enter" />
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Title order={4}>Media</Title>
            <TagsInput label="Image URLs" value={form.imageUrls} onChange={v => setField('imageUrls', v)} placeholder="Enter URL and press Enter" />
            <TagsInput label="Video URLs" value={form.videoUrls} onChange={v => setField('videoUrls', v)} placeholder="Enter URL and press Enter" />
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Title order={4}>Episode Times (Series)</Title>
            <TextInput label="Last Episode Time (RFC3339)" placeholder="2025-01-15T00:00:00Z" value={form.lastEpisodeTime} onChange={e => setField('lastEpisodeTime', e.target.value)} />
            <TextInput label="Next Episode Time (RFC3339)" placeholder="2025-01-22T00:00:00Z" value={form.nextEpisodeTime} onChange={e => setField('nextEpisodeTime', e.target.value)} />
          </Stack>
        </Paper>

        {error && <Text c="red">{typeof error === 'string' ? error : JSON.stringify(error)}</Text>}
        {success && <Text c="green">{success}</Text>}

        <Button type="submit" loading={loading} size="md">{submitLabel}</Button>
      </Stack>
    </form>
  );
};

export const defaultFormState = {
  name: '',
  description: '',
  ruName: '',
  ruDescription: '',
  wikiArticleLink: '',
  wikiArticleID: '',
  category: '1',
  thumbnailUrl: '',
  ruWikiArticleLink: '',
  author: '',
  publisher: '',
  otherPlatformsLinks: [],
  year: null,
  subCategory: '',
  genres: [],
  country: '',
  duration: null,
  actors: [],
  imageUrls: [],
  videoUrls: [],
  lastEpisodeTime: '',
  nextEpisodeTime: '',
};

export function proposalToFormState(p) {
  return {
    name: p.name || '',
    description: p.description || '',
    ruName: p.ruName || '',
    ruDescription: p.ruDescription || '',
    wikiArticleLink: p.wikiArticleLink || '',
    wikiArticleID: p.wikiArticleID || '',
    category: String(p.category ?? 1),
    thumbnailUrl: p.thumbnailUrl || '',
    ruWikiArticleLink: p.ruWikiArticleLink || '',
    author: p.author || '',
    publisher: p.publisher || '',
    otherPlatformsLinks: p.otherPlatformsLinks || [],
    year: p.year || null,
    subCategory: p.subCategory || '',
    genres: p.genres || [],
    country: p.country || '',
    duration: p.duration || null,
    actors: p.actors || [],
    imageUrls: p.imageUrls || [],
    videoUrls: p.videoUrls || [],
    lastEpisodeTime: p.lastEpisodeTime && p.lastEpisodeTime !== '0001-01-01T00:00:00Z' ? p.lastEpisodeTime : '',
    nextEpisodeTime: p.nextEpisodeTime && p.nextEpisodeTime !== '0001-01-01T00:00:00Z' ? p.nextEpisodeTime : '',
  };
}

export default ArtForm;
